// components/Survey/SurveyRunner.jsx — full answering experience: banner, progress, validation, submit, thank-you
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, Spinner, Text } from "@fluentui/react-components";
import { Send24Regular, CheckmarkCircle48Filled, LockClosed24Regular, Clock24Regular, ArrowCounterclockwise24Regular } from "@fluentui/react-icons";
import SurveyForm from "./SurveyForm";
import { C } from "./surveyUI";
import { countQuestions, estimateMinutes, formatDate, isVisible, hasAnswer, validateAnswers, pruneHiddenAnswers } from "./surveyUtils";

function loadDraft(key) {
  if (!key) return null;
  try { return JSON.parse(localStorage.getItem(key) || "null"); } catch { return null; }
}

function saveDraft(key, answers) {
  if (!key) return;
  try { localStorage.setItem(key, JSON.stringify(answers)); } catch {}
}

function clearDraft(key) {
  if (!key) return;
  try { localStorage.removeItem(key); } catch {}
}

/**
 * Props:
 *   survey          survey object
 *   mode            "take" | "preview" | "review"
 *   initialAnswers  answers to show in review mode
 *   draftKey        localStorage key for autosaving in-progress answers (take mode)
 *   dueDate         ISO date shown in the banner
 *   onSubmit        async (answers, durationSec) => void
 *   footer          node rendered under the thank-you card
 *   notice          node rendered between banner and questions (review / closed banners)
 *   stickyTop       px offset for the sticky progress bar (below whatever header sits above it)
 */
export default function SurveyRunner({ survey, mode = "take", initialAnswers, draftKey, dueDate, onSubmit, footer, notice, stickyTop = 60 }) {
  const readOnly = mode === "review";
  const [answers, setAnswers] = useState(() => initialAnswers || loadDraft(draftKey) || {});
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [done, setDone] = useState(false);
  const startedAt = useRef(Date.now());
  const restored = useRef(!initialAnswers && !!loadDraft(draftKey));

  useEffect(() => { if (mode === "take") saveDraft(draftKey, answers); }, [answers, draftKey, mode]);

  const progress = useMemo(() => {
    const visible = survey.questions.filter((q) => q.type !== "section" && isVisible(q, answers));
    const answered = visible.filter((q) => hasAnswer(q, answers[q.id])).length;
    return { answered, total: visible.length, pct: visible.length ? (answered / visible.length) * 100 : 0 };
  }, [survey.questions, answers]);

  const onChange = (key, value) => {
    setAnswers((prev) => ({ ...prev, [key]: value }));
    const qid = key.split("__other__")[0];
    if (errors[qid]) setErrors((prev) => { const n = { ...prev }; delete n[qid]; return n; });
  };

  const submit = async () => {
    const errs = validateAnswers(survey.questions, answers);
    setErrors(errs);
    const first = survey.questions.find((q) => errs[q.id]);
    if (first) {
      document.getElementById(`sq-${first.id}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setSubmitting(true);
    setSubmitError("");
    try {
      await onSubmit?.(pruneHiddenAnswers(survey.questions, answers), (Date.now() - startedAt.current) / 1000);
      clearDraft(draftKey);
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setSubmitError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const resetPreview = () => { setAnswers({}); setErrors({}); setDone(false); startedAt.current = Date.now(); };

  if (done) {
    return (
      <div style={{ maxWidth: 760, margin: "0 auto" }}>
        <div style={{ background: "#fff", borderRadius: 16, border: `1px solid ${C.line}`, padding: "48px 32px", textAlign: "center", boxShadow: "0 10px 40px rgba(0,26,61,0.08)" }}>
          <CheckmarkCircle48Filled style={{ color: "#2E8540", fontSize: 64 }} />
          <Text style={{ fontSize: 22, fontWeight: 800, color: C.navy, display: "block", margin: "12px 0 6px" }}>
            {mode === "preview" ? "Preview submitted" : "Thank you!"}
          </Text>
          <Text style={{ fontSize: 14, color: C.ink2, display: "block", lineHeight: 1.7, maxWidth: 480, margin: "0 auto", whiteSpace: "pre-line" }}>
            {mode === "preview"
              ? "Nothing was saved — this is how staff will see the confirmation."
              : survey.settings?.thankYouMessage || "Your response has been recorded. We appreciate you taking the time to share your feedback."}
          </Text>
          {mode === "preview" && <Button style={{ marginTop: 20 }} icon={<ArrowCounterclockwise24Regular />} onClick={resetPreview}>Start over</Button>}
          {footer && <div style={{ marginTop: 24 }}>{footer}</div>}
        </div>
      </div>
    );
  }

  const n = countQuestions(survey.questions);
  return (
    <div style={{ maxWidth: 760, margin: "0 auto" }}>
      {/* Banner */}
      <div style={{ borderRadius: 16, overflow: "hidden", background: "#fff", border: `1px solid ${C.line}`, boxShadow: "0 6px 24px rgba(0,26,61,0.06)", marginBottom: 14 }}>
        <div style={{ background: `linear-gradient(135deg, ${C.navy} 0%, ${C.navy2} 60%, #0052CC 100%)`, padding: "28px 28px 24px", position: "relative" }}>
          <div style={{ position: "absolute", right: -40, top: -60, width: 220, height: 220, borderRadius: "50%", background: "rgba(255,255,255,0.05)" }} />
          {survey.category && <Text style={{ color: "#9CC0FF", fontSize: 11, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase", display: "block", marginBottom: 6 }}>{survey.category}</Text>}
          <Text style={{ color: "#fff", fontSize: 24, fontWeight: 800, display: "block", lineHeight: 1.25 }}>{survey.title || "Untitled survey"}</Text>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 14 }}>
            {[
              `${n} question${n === 1 ? "" : "s"}`,
              `About ${estimateMinutes(survey.questions)} min`,
              survey.settings?.anonymous ? "Anonymous" : null,
              dueDate ? `Respond by ${formatDate(dueDate)}` : null,
            ].filter(Boolean).map((t) => (
              <span key={t} style={{ fontSize: 12, color: "#fff", background: "rgba(255,255,255,0.14)", padding: "4px 10px", borderRadius: 20, display: "inline-flex", alignItems: "center", gap: 4 }}>
                {t.startsWith("Anonymous") && <LockClosed24Regular style={{ fontSize: 13 }} />}
                {t.startsWith("Respond") && <Clock24Regular style={{ fontSize: 13 }} />}
                {t}
              </span>
            ))}
          </div>
        </div>
        {(survey.description || survey.settings?.anonymous) && (
          <div style={{ padding: "18px 28px" }}>
            {survey.description && <Text style={{ fontSize: 14, color: C.ink, lineHeight: 1.7, display: "block", whiteSpace: "pre-line" }}>{survey.description}</Text>}
            {survey.settings?.anonymous && (
              <Text style={{ fontSize: 12, color: "#5B3DC4", display: "block", marginTop: survey.description ? 10 : 0 }}>
                Your name will not be shown alongside your answers in any report.
              </Text>
            )}
          </div>
        )}
      </div>

      {notice}

      {restored.current && mode === "take" && (
        <div style={{ background: "#F0F6FF", border: "1px solid #C7DBFF", borderRadius: 10, padding: "10px 14px", fontSize: 13, color: "#1B3A6B", marginBottom: 12, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
          We restored the answers you started earlier on this device.
          <Button size="small" appearance="subtle" onClick={() => { setAnswers({}); restored.current = false; }}>Clear</Button>
        </div>
      )}

      {/* Progress */}
      {!readOnly && survey.settings?.showProgress !== false && (
        <div style={{ position: "sticky", top: stickyTop, zIndex: 5, background: "rgba(243,242,241,0.94)", backdropFilter: "blur(6px)", padding: "10px 0", marginBottom: 4 }}>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: C.ink2, marginBottom: 6 }}>
            <span>{progress.answered} of {progress.total} answered</span>
            <span style={{ fontWeight: 700 }}>{Math.round(progress.pct)}%</span>
          </div>
          <div style={{ height: 6, background: "#E1E4EA", borderRadius: 6, overflow: "hidden" }}>
            <div style={{ height: "100%", width: `${progress.pct}%`, background: "linear-gradient(90deg, #0052CC, #00A3C4)", borderRadius: 6, transition: "width 0.3s ease" }} />
          </div>
        </div>
      )}

      <SurveyForm survey={survey} answers={answers} onChange={onChange} errors={errors} readOnly={readOnly} />

      {!readOnly && (
        <div style={{ marginTop: 18, padding: "18px 22px", background: "#fff", borderRadius: 12, border: `1px solid ${C.line}`, display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: 200 }}>
            {Object.keys(errors).length > 0
              ? <Text style={{ fontSize: 13, color: C.danger, fontWeight: 600 }}>{Object.keys(errors).length} question{Object.keys(errors).length === 1 ? " needs" : "s need"} your attention</Text>
              : <Text style={{ fontSize: 13, color: C.ink2 }}>{mode === "preview" ? "Preview mode — submitting will not save anything." : "Please review your answers. You can only submit once."}</Text>}
            {submitError && <Text style={{ fontSize: 13, color: C.danger, display: "block", marginTop: 4 }}>{submitError}</Text>}
          </div>
          <Button appearance="primary" size="large" icon={submitting ? <Spinner size="tiny" appearance="inverted" /> : <Send24Regular />} disabled={submitting} onClick={submit} style={{ background: C.brand, minWidth: 160 }}>
            {submitting ? "Submitting…" : "Submit"}
          </Button>
        </div>
      )}
      {readOnly && footer && <div style={{ marginTop: 18 }}>{footer}</div>}
    </div>
  );
}
